import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, FormHelperText, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import MailOutlined from '@mui/icons-material/MailOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import UserAuthService from '../../../../shared/services/user-auth/user-auth.service';
import { IRegisterDetails } from '../../../../shared/services/user-auth/user-auth.model';
import SsoService from '../../../../shared/services/sso/sso.service';
import { AuthMode, AuthModeToggle } from './auth-mode-toggle';
import { AuthLayout } from './auth-layout';
import { AuthField } from './auth-field';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth-branding';
import { useAuthBranding } from './use-auth-branding';

// Company-email guard carried over from the previous signup page: block generic
// @gmail.com addresses (with a single legacy allowance).
const isCompanyEmail = (value?: string): boolean => {
  if (!value) return true;
  const lower = value.toLowerCase();
  if (lower.endsWith('@gmail.com')) return lower.startsWith('sjnaunton+');
  return true;
};

// Plain-English copy for the ?ssoError codes the backend redirects back with.
const ssoErrorMessage = (code: string): string => {
  switch (code) {
    case 'wrong_tenant':
      return 'That Microsoft account is not part of your organisation. Please sign in with your work account.';
    case 'no_account':
      return 'We could not find an account for you. Please contact your administrator.';
    case 'sso_not_enabled':
    case 'sso_not_configured':
      return 'Single sign-on is not set up for your organisation yet.';
    case 'idp_error':
      return 'Sign-in was cancelled or did not complete. Please try again.';
    case 'email_mismatch':
      return 'You signed in with a different account than this sign-in was for. Please sign in with the email you were invited with.';
    default:
      return 'Single sign-on could not be completed. Please try again.';
  }
};

interface AuthPageProps {
  initialMode: AuthMode;
}

/**
 * Single auth screen serving both sign up and sign in via a client-side mode
 * toggle. Mounted at /signup (signup) and /login (signin). See
 * design_handoff_auth_redesign/README.md.
 */
export const AuthPage = ({ initialMode }: AuthPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const userAuthService = new UserAuthService();
  const ssoService = new SsoService();
  const userDetailsContext = useContext(UserDetailsContext);
  const userNotificationsContext = useContext(UserNotificationsContext);

  const branding = useAuthBranding();

  // A partner may disable signup: force signin and hide the toggle + cross-link.
  const [mode, setMode] = useState<AuthMode>(branding.allowSignup ? initialMode : 'signin');
  const [errorMessage, setErrorMessage] = useState('');
  // Email-first sign-in: collect the email, resolve password vs SSO, then reveal
  // the password field in place (kept mounted throughout for password managers).
  const [signinStep, setSigninStep] = useState<'email' | 'password'>('email');
  // Set to the SSO start URL when the org has SSO enabled but not enforced, so we
  // offer a choice rather than auto-redirecting.
  const [ssoOption, setSsoOption] = useState<string | null>(null);
  // True once we hand off to an external SSO redirect. Formik clears isSubmitting
  // the moment onSubmit returns, but the window.location navigation to the identity
  // provider takes a beat, so this keeps the button disabled + spinner until the
  // page actually unloads (otherwise it flicks back to an enabled "Continue").
  const [redirecting, setRedirecting] = useState(false);

  const isSignup = mode === 'signup';

  // Where to land after sign-in: the deep link the guard bounced from, or a
  // ?returnTo, else the dashboard. Threaded through the SSO round trip.
  const returnTo = ((location.state as any)?.from?.pathname as string) || searchParams.get('returnTo') || '/dashboard';

  // Already authenticated: skip the auth screen.
  useEffect(() => {
    if (userDetailsContext.userDetails) {
      navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Surface a branded message when returning from a failed SSO round trip.
  useEffect(() => {
    const code = searchParams.get('ssoError');
    if (code) {
      setErrorMessage(ssoErrorMessage(code));
      searchParams.delete('ssoError');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = useMemo(() => {
    if (mode === 'signin') {
      return Yup.object({
        email: Yup.string().email('Email is invalid').required('Email is required'),
        // Password is only required once the email step has resolved to a password
        // sign-in, so "Continue" validates on the email alone.
        password: signinStep === 'password'
          ? Yup.string().min(8, 'Your password must be at least 8 characters').required('Password is required')
          : Yup.string(),
      });
    }
    return Yup.object({
      email: Yup.string()
        .email('Email is invalid')
        .test('is-company-email', 'Please use your company email address', isCompanyEmail)
        .required('Email is required'),
    });
  }, [mode, signinStep]);

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema,
    onSubmit: async (values) => {
      setErrorMessage('');
      // The backend matches emails case-insensitively; normalize here too so
      // what the user sees submitted is what gets stored on sign-up.
      const email = values.email.trim().toLowerCase();
      if (mode === 'signin') {
        // Email step: resolve whether this account uses a password or SSO.
        if (signinStep === 'email') {
          try {
            const result = await ssoService.getAuthMethod(email, returnTo);
            if (result.method === 'sso' && result.ssoStartUrl) {
              if (result.enforced) {
                // Mandatory SSO: go straight to the identity provider.
                setRedirecting(true);
                window.location.href = result.ssoStartUrl;
                return;
              }
              // Optional SSO: offer a choice of SSO or a password.
              setSsoOption(result.ssoStartUrl);
              return;
            }
          } catch (err) {
            // Fall through to password entry rather than blocking sign-in.
          }
          setSigninStep('password');
          setTimeout(() => document.getElementById('password')?.focus(), 0);
          return;
        }
        // Password step.
        try {
          const loginResult = await userAuthService.loginUser({ email, password: values.password });
          // MFA-enabled accounts pause here: the session is held pending until a
          // second factor verifies on the challenge screen.
          if (loginResult?.mfaRequired) {
            navigate('/login/verify', { state: { methods: loginResult.methods } });
            return;
          }
          const updated = await userDetailsContext.forceUpdateUserDetails();
          // Non-partners land on /dashboard, which resolves the user's chosen home view (see
          // HomeDashboardRoute in App.tsx). Keeping that logic in one place means every route to
          // /dashboard honours the setting, not just this one.
          navigate(updated?.isPartner ? '/partner-dashboard' : '/dashboard', updated?.isPartner ? undefined : { state: { justAuthenticated: true } });
        } catch (err: any) {
          // Enforced-SSO orgs reject password login: send the user back to the
          // email step so Continue takes them to their identity provider.
          if (err?.response?.status === 403 && err?.response?.data?.ssoRequired) {
            setSigninStep('email');
            formik.setFieldValue('password', '');
            setErrorMessage('Your organisation requires single sign-on. Select Continue to sign in with your organisation account.');
            return;
          }
          setErrorMessage(err?.response?.data?.message || 'Unable to sign in. Please try again.');
        }
      } else {
        try {
          // The register endpoint only requires the email at this step.
          await userAuthService.registerUser({ email } as IRegisterDetails);
          userDetailsContext.forceUpdateUserDetails();
          navigate('/email-verification');
        } catch (err: any) {
          if (err?.response?.data?.message) {
            setErrorMessage(err.response.data.message);
          } else {
            userNotificationsContext.openSnackbar({
              open: true,
              message: 'There was an error, please try again',
              backgroundColor: 'text.primary',
            });
          }
        }
      }
    },
  });

  // Busy = a submit in flight, or an SSO redirect handed off and now unloading.
  const isBusy = formik.isSubmitting || redirecting;

  // Keep the label centred in the button and float the busy spinner just to its
  // left (absolutely positioned, so it never nudges the text).
  const buttonLabel = (text: string) => (
    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      {isBusy && (
        // Centre vertically with auto margins, not translateY: CircularProgress
        // owns the root transform for its spin animation, so a transform here is
        // overridden and the spinner drops out of line with the text.
        <CircularProgress
          size={14}
          color="inherit"
          sx={{ position: 'absolute', right: 'calc(100% + 8px)', top: 0, bottom: 0, my: 'auto' }}
        />
      )}
      {text}
    </Box>
  );

  // Friendlier validation: don't flash a field red just for being focused and
  // left empty. Never nag "required" on blur. On blur, only surface an error
  // for a value the user actually typed that is still invalid (say a malformed
  // email). After a submit attempt, show everything, then it clears live as
  // they fix it.
  const fieldError = (field: 'email' | 'password'): string | undefined => {
    const message = formik.errors[field];
    if (!message) return undefined;
    const settledWithContent = formik.touched[field] && formik.values[field].length > 0;
    return formik.submitCount > 0 || settledWithContent ? message : undefined;
  };

  const changeMode = (next: AuthMode) => {
    if (next === mode || !branding.allowSignup) return;
    setMode(next);
    setErrorMessage('');
    setSigninStep('email');
    setSsoOption(null);
    // Reset validation state (touched, errors, and the submit-attempt flag) so
    // switching modes never carries a red field across; keep the typed email.
    formik.resetForm({ values: { email: formik.values.email, password: '' } });
    // Keep the URL deep-linkable without a route change (preserves the toggle
    // animation and typed email).
    window.history.replaceState(null, '', next === 'signup' ? '/signup' : '/login');
  };

  return (
    <AuthLayout branding={branding}>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ width: '100%' }}>
        {branding.allowSignup && <AuthModeToggle mode={mode} onChange={changeMode} />}

          <Typography
            component="h2"
            sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
          >
            {isSignup ? 'Start your free trial' : 'Welcome back'}
          </Typography>
          <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
            {isSignup ? '14 days of full access. No card required.' : `Sign in to your ${branding.productName} workspace.`}
          </Typography>

          <Stack spacing={2}>
            <AuthField
              id="email"
              name="email"
              label="Work email"
              type="email"
              placeholder="you@company.com"
              autoComplete="email"
              icon={<MailOutlined />}
              value={formik.values.email}
              onChange={(e) => {
                formik.handleChange(e);
                // Changing the email invalidates the earlier password-or-SSO
                // decision, so drop back to the email step (clearing the password
                // and any error) and let the next Continue re-check single sign-on
                // for the new address.
                if (ssoOption) setSsoOption(null);
                if (signinStep === 'password') { setSigninStep('email'); formik.setFieldValue('password', ''); }
                if (errorMessage) setErrorMessage('');
              }}
              onBlur={formik.handleBlur}
              error={Boolean(fieldError('email'))}
              helperText={fieldError('email')}
              immediate={formik.submitCount > 0}
              focusRing={branding.focusRing}
            />

            {!isSignup && (
              // Kept mounted throughout (just hidden until the email step resolves
              // to a password sign-in) so password managers can fill it.
              <Box sx={{ display: signinStep === 'password' ? 'block' : 'none' }}>
                <AuthField
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  icon={<LockOutlined />}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(fieldError('password'))}
                  helperText={fieldError('password')}
                  immediate={formik.submitCount > 0}
                  focusRing={branding.focusRing}
                  labelAction={
                    <MuiLink
                      component={Link}
                      to="/forgot-password"
                      underline="none"
                      sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'primary.main' }}
                    >
                      Forgot password?
                    </MuiLink>
                  }
                />
                <MuiLink
                  component="button"
                  type="button"
                  onClick={() => { setSigninStep('email'); formik.setFieldValue('password', ''); setErrorMessage(''); }}
                  underline="none"
                  sx={{ display: 'block', mt: 1, fontSize: '0.8125rem', fontWeight: 500, color: 'primary.main', textAlign: 'left' }}
                >
                  Use a different email
                </MuiLink>
              </Box>
            )}
          </Stack>

          {errorMessage && (
            <FormHelperText error role="alert" sx={{ mt: 1.5, ml: '2px', fontSize: '0.8125rem' }}>
              {errorMessage}
            </FormHelperText>
          )}

          {!isSignup && ssoOption ? (
            // Optional SSO (enabled, not enforced): let the user choose single
            // sign-on or fall back to a password.
            <>
              <Button
                type="button"
                fullWidth
                size="large"
                disabled={isBusy}
                onClick={() => { setRedirecting(true); window.location.href = ssoOption; }}
                sx={{ mt: '34px', height: 50, fontSize: '0.9375rem' }}
              >
                {buttonLabel('Continue with single sign-on')}
              </Button>
              <Typography sx={{ mt: '16px', textAlign: 'center', fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => { setSsoOption(null); setSigninStep('password'); setTimeout(() => document.getElementById('password')?.focus(), 0); }}
                  sx={{ border: 'none', background: 'none', p: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 500, color: 'primary.main' }}
                >
                  Sign in with a password instead
                </Box>
              </Typography>
            </>
          ) : (
            <Button
              type="submit"
              fullWidth
              size="large"
              disabled={isBusy}
              // Field-to-button gap per the prototype: 26px in sign-up, 34px in sign-in.
              sx={{ mt: isSignup ? '26px' : '34px', height: 50, fontSize: '0.9375rem' }}
            >
              {buttonLabel(isSignup ? 'Sign up' : signinStep === 'email' ? 'Continue' : 'Sign in')}
            </Button>
          )}

          {branding.allowSignup && (
            <Typography sx={{ mt: '22px', textAlign: 'center', fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary }}>
              {isSignup ? 'Already have an account? ' : `New to ${branding.productName}? `}
              <Box
                component="button"
                type="button"
                onClick={() => changeMode(isSignup ? 'signin' : 'signup')}
                sx={{
                  border: 'none',
                  background: 'none',
                  p: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 500,
                  color: 'primary.main',
                }}
              >
                {isSignup ? 'Sign in' : 'Start your free trial'}
              </Box>
            </Typography>
          )}
      </Box>
    </AuthLayout>
  );
};
